import { DialogActions, DialogContent } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { parseErrorMessage } from '../../../../../core/api/api.context';
import {
  ArchiveOrderActionHandlers,
  ArchiveOrderActionProps,
  ArchiveOrderActionState,
  useArchiveOrderAction
} from './archive-order-action.hook';

export function ArchiveOrderModal({ order, ...rest }: ArchiveOrderActionProps) {
  const [state, handlers, canArchive] = useArchiveOrderAction({ order, ...rest });

  return <ArchiveOrderModalComponent state={state} handlers={handlers} canArchive={canArchive} />;
}

/* This component is exported only for use in Storybook */
export function ArchiveOrderModalComponent({
  handlers,
  state,
  canArchive
}: {
  handlers: ArchiveOrderActionHandlers;
  state: ArchiveOrderActionState;
  canArchive: boolean;
}) {
  const callToActionButtonHandler =
    state.current === 'ARCHIVE_REQUEST_SUCCESS' ? handlers.onSuccessArchiveOrder : handlers.onConfirmArchiveOrder;

  return (
    <Modal title="Archive order?" open={true} loading={state.loading} maxWidth="xs" onClose={handlers.onCancelIntent}>
      <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
        {state.error && (
          <p>
            <Alert variant="outlined" severity="error">
              <AlertTitle>Something went wrong</AlertTitle>
              {parseErrorMessage(state.error.message)}
            </Alert>
          </p>
        )}
        {state.current === 'ARCHIVE_REQUEST_SUCCESS' ? (
          <p>
            <Alert variant="outlined" severity="success">
              <AlertTitle>The order has been successfully archived</AlertTitle>
              Once you close this modal, you will return to the order list page.
            </Alert>
          </p>
        ) : (
          <ArchiveOrderModalText canArchive={canArchive} />
        )}
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        {state.current !== 'ARCHIVE_REQUEST_SUCCESS' && (
          <TextButton color="secondary" variant="outlined" onClick={() => handlers.onCancelIntent()}>
            {canArchive ? 'Cancel' : 'Close'}
          </TextButton>
        )}
        {state.current === 'ARCHIVE_REQUEST_SUCCESS' ? (
          <TextButton key="doneButton" color="secondary" variant="outlined" onClick={() => callToActionButtonHandler()}>
            Done
          </TextButton>
        ) : canArchive ? (
          <TextButton key="archiveButton" color="danger" variant="outlined" onClick={() => callToActionButtonHandler()}>
            Archive
          </TextButton>
        ) : null}
      </DialogActions>
    </Modal>
  );
}

export function ArchiveOrderModalText({ canArchive }: { canArchive: boolean }) {
  return (
    <>
      <p>Archiving will hide the order from the orders list and prevent it from being counted in the statistics.</p>
      {canArchive ? (
        <p> Are you sure?</p>
      ) : (
        <p>You will need to collect, cancel or retrieve the order's deliveries before you can do this.</p>
      )}
    </>
  );
}
